import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { CreditCard, Gauge, Package, Wrench } from 'lucide-react'
import { PageHeader } from '@/components/layout'
import { Button, Select } from '@/components/ui'
import { useAuth } from '@/hooks/useAuth'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDashboard } from '@/hooks/useDashboard'
import { useAllProperties } from '@/hooks/useProperties'
import { useIsDesktop } from '@/hooks/useMediaQuery'
import { DashboardPropertyContext } from '@/hooks/useDashboardScope'
import { DashboardChecklist } from '@/components/dashboard/DashboardChecklist'
import QuickActionBar from '@/components/dashboard/QuickActionBar'
import { TodayStrip } from '@/components/dashboard/TodayStrip'
import { ShiftClockCard } from '@/components/dashboard/ShiftClockCard'
import { TeamTodayCard } from '@/components/dashboard/TeamTodayCard'
import { TodoCard, type DashboardRole } from '@/components/dashboard/TodoCard'
import { OwnerMoney } from '@/components/dashboard/OwnerMoney'
import { AccountantBooks } from '@/components/dashboard/AccountantBooks'
import { BlockerQuestionModal } from '@/components/onboarding/BlockerQuestionModal'

/**
 * EP-155 (owner, 2026-10-07): one page per role, built from the same blocks.
 *
 *   owner       setup (while onboarding) · to do · the business figures · team
 *   admin       clock · to do · team · visitors
 *   staff       clock · quick actions (desktop) · to do · visitors · bottom bar (phone)
 *   accountant  to do · the month being closed · one line of this period's money
 *
 * Action before figures, one fact in one place, every block one line or a
 * short card. The clock button sits in the header on a desktop (D8) and is a
 * full-width button under the greeting on a phone. The building picker shows
 * only with two or more buildings (D10). Mockups: docs/ep-155-dashboard-redesign.md §14.
 */

// The staff's quick actions on a desktop; a phone has the bottom bar instead.
function StaffQuickActions() {
  const { t } = useTranslation('common')
  const { data: accountSettings } = useAccountSettings()
  // Condominium accounts have no meters (same gate as QuickActionBar).
  const isCondominium = accountSettings?.account_type === 'condominium'
  return (
    <div className="flex flex-wrap gap-2" data-testid="staff-desktop-quick-actions">
      <Button as={Link} to="/payments/record" variant="outline" size="sm" leftIcon={<CreditCard className="w-4 h-4" />} data-testid="staff-qa-payment">
        {t('dashboard.quick_actions.record_payment')}
      </Button>
      <Button as={Link} to="/maintenances/new" variant="outline" size="sm" leftIcon={<Wrench className="w-4 h-4" />} data-testid="staff-qa-maintenance">
        {t('dashboard.quick_actions.log_maintenance')}
      </Button>
      {!isCondominium && (
        <Button as={Link} to="/meter-readings" variant="outline" size="sm" leftIcon={<Gauge className="w-4 h-4" />} data-testid="staff-qa-reading">
          {t('dashboard.quick_actions.add_meter_reading')}
        </Button>
      )}
      <Button as={Link} to="/parcels/new" variant="outline" size="sm" leftIcon={<Package className="w-4 h-4" />} data-testid="staff-qa-parcel">
        {t('dashboard.quick_actions.add_parcel')}
      </Button>
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const { data: accountSettings } = useAccountSettings()
  const isDesktop = useIsDesktop()
  // 2026-10-05 (owner): the dashboard opens on all buildings and keeps its own
  // choice in the URL (so Back restores it), never reading or writing the
  // header's selection; every block gets it through DashboardPropertyContext.
  // null means all buildings.
  const [searchParams, setSearchParams] = useSearchParams()
  const rawPropertyId = Number(searchParams.get('property_id'))
  const selectedPropertyId = rawPropertyId > 0 ? rawPropertyId : null
  const setSelectedPropertyId = (id: number | null) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      if (id) next.set('property_id', String(id))
      else next.delete('property_id')
      return next
    }, { replace: true })
  }
  const { data: properties = [] } = useAllProperties()
  const { data: stats, isLoading, isFetching } = useDashboard(selectedPropertyId)

  // Read off the loaded user: while the session loads nothing role-specific
  // renders, so an owner's page never flashes the clock.
  const role = user?.role as DashboardRole | undefined
  const clocks = role === 'staff' || role === 'admin'
  // Known only once the figures load; a page never claims "no building" early.
  const noBuilding = stats !== undefined && stats.total_properties === 0

  // Greet the user, not the workspace (account names can read as system ids).
  const greetingName = user?.first_name || accountSettings?.name

  const picker = properties.length > 1 ? (
    <Select
      data-testid="dashboard-property-picker"
      aria-label={t('labels.select_property')}
      value={selectedPropertyId ? String(selectedPropertyId) : ''}
      onChange={(e) => setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)}
      options={[
        { value: '', label: t('labels.all_properties') },
        ...properties.map((p) => ({ value: String(p.id), label: p.name })),
      ]}
      className="w-40 max-w-[10rem]"
      size="sm"
    />
  ) : undefined

  return (
    <DashboardPropertyContext.Provider value={selectedPropertyId}>
      {/* pb-24 on a phone: content clears the staff bottom bar. */}
      <div className={`flex flex-col gap-4 ${role === 'staff' ? 'pb-24 lg:pb-0' : ''}`} data-testid={role ? `dashboard-${role}` : undefined}>
        <PageHeader
          title={t('dashboard.title')}
          description={greetingName ? (
            <span className="hidden sm:inline">{t('dashboard.welcome', { name: greetingName })}</span>
          ) : undefined}
          isRefreshing={isFetching && !isLoading}
          titleActionOnDesktop
          titleAction={picker || (clocks && isDesktop) ? (
            // On a desktop the clock button sits beside the picker, top right,
            // on the title's line (D8).
            <div className="flex items-center gap-2">
              {picker}
              {clocks && isDesktop && <ShiftClockCard />}
            </div>
          ) : undefined}
        />

        {/* Compact phone greeting; sm and up have it in the header. */}
        {greetingName && (
          <div className="sm:hidden -mt-2" data-testid="dashboard-greeting">
            <h1 className="text-base font-semibold">{t('dashboard.welcome_short', { name: greetingName })}</h1>
            {accountSettings?.name && <p className="text-xs text-base-content/60 mt-0.5">{accountSettings.name}</p>}
          </div>
        )}

        {clocks && !isDesktop && <ShiftClockCard />}

        {role === 'staff' && isDesktop && <StaffQuickActions />}

        {role === 'owner' && <DashboardChecklist />}

        {/* A brand-new account: the owner has only the setup card above; the
            other roles get one line saying who sets it up. */}
        {noBuilding && role !== 'owner' && role !== undefined && (
          <p className="rounded-lg border border-base-200 bg-base-100 px-4 py-3 text-sm text-base-content/70" data-testid="dashboard-no-building">
            {t('dashboard.no_building')}
          </p>
        )}

        {role && !noBuilding && (
          <>
            <TodoCard role={role} />
            {/* The figures wait for a unit: an empty building reads ฿0 everywhere. */}
            {role === 'owner' && (isLoading || (stats?.total_units ?? 0) > 0) && <OwnerMoney />}
            {role === 'accountant' && <AccountantBooks />}
            {(role === 'owner' || role === 'admin') && <TeamTodayCard />}
            {(role === 'admin' || role === 'staff') && <TodayStrip />}
          </>
        )}

        {/* Fixed bottom bar: staff only, phone only; mounted here so it leaves with the page. */}
        <QuickActionBar />

        {/* EP-160 S8: "ติดตรงไหน?", owner and admin, once per trigger. */}
        <BlockerQuestionModal />
      </div>
    </DashboardPropertyContext.Provider>
  )
}
